/** Játékosfegyverek és lövedékek fix tickes kezelése. */

import * as THREE from 'three'
import type { Actor, Ctx, DamageKind, System } from '../core/contracts'
import { DEG } from '../core/math'
import { PROJECTILES } from '../content/enemies'
import { WEAPONS, WEAPON_ORDER, type WeaponArchetype, type WeaponId } from '../content/weapons'
import type { PlayerSystem } from '../player/player'

interface ProjectileState {
  ownerId: number
  damage: number
  damageKind: DamageKind
  splashRadius: number
  splashDamage: number
  lifetime: number
  /** Játékosfegyvernél a GDD-ben rögzített tényleges maximális hatótáv. */
  rangeRemaining: number | null
  color: number
}

const _eye = new THREE.Vector3()
const _dir = new THREE.Vector3()
const _right = new THREE.Vector3()
const _up = new THREE.Vector3()
const _step = new THREE.Vector3()

export class CombatSystem implements System {
  readonly name = 'combat'
  private weapon: WeaponId = 'pistol'
  private cooldown = 0
  private readonly projectileIds = new Set<number>()
  private unsubscribe: (() => void) | null = null

  constructor(private readonly playerSystem: PlayerSystem) {}

  init(ctx: Ctx): void {
    ctx.view.equip(this.weapon)
    this.unsubscribe = ctx.events.on('spawn', ({ actor }) => {
      if (actor.kind === 'projectile') this.projectileIds.add(actor.id)
    })
  }

  dispose(): void {
    this.unsubscribe?.()
    this.unsubscribe = null
    this.projectileIds.clear()
  }

  fixedUpdate(ctx: Ctx): void {
    this.updateProjectiles(ctx)
    if (!ctx.actors.player.alive) return

    if (this.cooldown > 0) this.cooldown--
    this.selectWeapon(ctx)
    this.syncHud(ctx)

    const def = WEAPONS[this.weapon]
    const wantsFire = def.automatic ? ctx.input.fire : ctx.input.firePressed
    if (wantsFire && this.cooldown === 0 && ctx.view.lowered < 0.1) {
      this.fire(ctx, def)
      this.syncHud(ctx)
    }
  }

  private selectWeapon(ctx: Ctx): void {
    const inv = this.playerSystem.inventory
    let wanted: WeaponId | null = null
    const slot = ctx.input.weaponSlot
    if (slot !== null) wanted = WEAPON_ORDER.find((id) => WEAPONS[id].slot === slot) ?? null

    if (ctx.input.weaponCycle !== 0) {
      const owned = WEAPON_ORDER.filter((id) => inv.weapons.has(id))
      const at = Math.max(0, owned.indexOf(this.weapon))
      const direction = ctx.input.weaponCycle > 0 ? 1 : -1
      wanted = owned[(at + direction + owned.length) % owned.length] ?? null
    }

    if (wanted && inv.weapons.has(wanted) && wanted !== this.weapon) {
      this.weapon = wanted
      this.cooldown = 0
      ctx.view.equip(wanted)
      ctx.fx.sound('weaponSwitch', null, 0.65)
    }
  }

  private syncHud(ctx: Ctx): void {
    const inv = this.playerSystem.inventory
    const def = WEAPONS[this.weapon]
    ctx.hud.weaponId = def.id
    ctx.hud.weaponName = def.name
    ctx.hud.ammo = def.ammo === null ? null : inv.ammo[def.ammo]
    ctx.hud.ammoMax = def.ammo === null ? null : inv.ammoMax[def.ammo]
    ctx.hud.ammoType = def.ammo ?? 'none'
    ctx.hud.weapons = WEAPON_ORDER.map((id) => {
      const weapon = WEAPONS[id]
      return {
        slot: weapon.slot,
        id,
        owned: inv.weapons.has(id),
        hasAmmo: weapon.ammo === null || inv.ammo[weapon.ammo] >= weapon.ammoPerShot,
      }
    })
    ctx.hud.fireProgress = def.fireInterval === 0 ? 1 : 1 - this.cooldown / def.fireInterval
  }

  private fire(ctx: Ctx, def: WeaponArchetype): void {
    const p = ctx.actors.player
    const inv = this.playerSystem.inventory
    if (def.ammo !== null && inv.ammo[def.ammo] < def.ammoPerShot) {
      ctx.fx.sound('emptyClick', null, 0.6)
      this.cooldown = Math.max(1, Math.round(0.35 / ctx.dt))
      return
    }
    if (def.ammo !== null) inv.ammo[def.ammo] -= def.ammoPerShot
    this.cooldown = def.fireInterval

    this.aim(ctx, _eye, _dir)
    const muzzle = ctx.view.muzzleWorld(new THREE.Vector3())
    ctx.view.fire(def.recoil)
    ctx.fx.sound(def.sound, null, def.soundVolume, def.soundPitch + ctx.rng.spread(def.soundPitchJitter))
    if (def.muzzleRadius > 0) ctx.fx.muzzleFlash(muzzle, _dir, def.muzzleColor, def.muzzleRadius)
    ctx.fx.shake(def.cameraShake)

    if (def.projectile) this.spawnProjectile(ctx, p, def, _eye, _dir)
    else if (def.damageKind === 'melee') this.melee(ctx, p, def, _eye, _dir)
    else this.hitscan(ctx, p, def, _eye, _dir, muzzle)
  }

  private aim(ctx: Ctx, origin: THREE.Vector3, out: THREE.Vector3): void {
    const p = ctx.actors.player
    origin.set(p.pos.x, p.pos.y + p.eyeHeight, p.pos.z)
    out.set(
      Math.sin(ctx.camera.yaw) * Math.cos(ctx.camera.pitch),
      Math.sin(ctx.camera.pitch),
      Math.cos(ctx.camera.yaw) * Math.cos(ctx.camera.pitch),
    ).normalize()
  }

  private spread(ctx: Ctx, base: THREE.Vector3, degrees: number, out: THREE.Vector3): THREE.Vector3 {
    if (degrees <= 0) return out.copy(base)
    _right.crossVectors(base, THREE.Object3D.DEFAULT_UP)
    if (_right.lengthSq() < 1e-6) _right.set(1, 0, 0)
    else _right.normalize()
    _up.crossVectors(_right, base).normalize()
    const spread = Math.tan(degrees * DEG)
    return out.copy(base).addScaledVector(_right, ctx.rng.spread(spread)).addScaledVector(_up, ctx.rng.spread(spread)).normalize()
  }

  private hitscan(ctx: Ctx, source: Actor, def: WeaponArchetype, origin: THREE.Vector3, baseDir: THREE.Vector3, muzzle: THREE.Vector3): void {
    for (let i = 0; i < def.pellets; i++) {
      const dir = def.pellets > 1
        ? this.shotgunSpread(ctx, baseDir, def.spreadDeg, i, def.pellets, new THREE.Vector3())
        : this.spread(ctx, baseDir, def.spreadDeg, new THREE.Vector3())
      const worldHit = ctx.world.raycast(origin, dir, def.range)
      const actorHit = ctx.actors.raycast(origin, dir, worldHit?.distance ?? def.range, source, 'enemy')
      if (actorHit) {
        ctx.actors.damage(actorHit.actor, ctx.rng.damage(...def.damage), source, dir.clone(), def.damageKind)
        ctx.fx.blood(actorHit.point, dir, def.pellets > 1 ? 4 : 12)
        ctx.fx.hitMarker(!actorHit.actor.alive)
        ctx.fx.tracer(muzzle, actorHit.point, 0xffe8b0, def.tracerWidth)
      } else if (worldHit) {
        ctx.fx.impact(worldHit.point, worldHit.normal, worldHit.impact)
        ctx.fx.tracer(muzzle, worldHit.point, 0xffe8b0, def.tracerWidth)
      }
    }
  }

  /** Egyenletes területeloszlású, szög szerint rétegzett sörétkör. */
  private shotgunSpread(ctx: Ctx, base: THREE.Vector3, degrees: number, pellet: number, count: number, out: THREE.Vector3): THREE.Vector3 {
    _right.crossVectors(base, THREE.Object3D.DEFAULT_UP)
    if (_right.lengthSq() < 1e-6) _right.set(1, 0, 0)
    else _right.normalize()
    _up.crossVectors(_right, base).normalize()
    const angle = (pellet + ctx.rng.next()) / count * Math.PI * 2
    const radius = Math.sqrt(ctx.rng.next()) * Math.tan(degrees * DEG)
    return out.copy(base)
      .addScaledVector(_right, Math.cos(angle) * radius)
      .addScaledVector(_up, Math.sin(angle) * radius)
      .normalize()
  }

  private melee(ctx: Ctx, source: Actor, def: WeaponArchetype, origin: THREE.Vector3, dir: THREE.Vector3): void {
    const wall = ctx.world.raycast(origin, dir, def.range)
    const hit = ctx.actors.raycast(origin, dir, wall?.distance ?? def.range, source, 'enemy')
    if (hit) {
      ctx.actors.damage(hit.actor, ctx.rng.damage(...def.damage), source, dir.clone(), 'melee')
      ctx.fx.blood(hit.point, dir, 10)
      ctx.fx.hitMarker(!hit.actor.alive)
    } else if (wall) {
      ctx.fx.impact(wall.point, wall.normal, wall.impact)
    }
  }

  private spawnProjectile(ctx: Ctx, source: Actor, weapon: WeaponArchetype, origin: THREE.Vector3, dir: THREE.Vector3): void {
    const projectile = PROJECTILES[weapon.projectile!]
    const state: ProjectileState = {
      ownerId: source.id,
      damage: ctx.rng.damage(...weapon.damage),
      damageKind: weapon.damageKind,
      splashRadius: projectile.splashRadius,
      splashDamage: projectile.splashDamage,
      lifetime: projectile.lifetime,
      rangeRemaining: weapon.range,
      color: projectile.color,
    }
    const actor = ctx.actors.spawn({
      kind: 'projectile', type: projectile.id, faction: source.faction,
      x: origin.x + dir.x * (source.radius + projectile.radius + 0.1), y: origin.y + dir.y * (source.radius + projectile.radius + 0.1), z: origin.z + dir.z * (source.radius + projectile.radius + 0.1),
      radius: projectile.radius, height: projectile.radius * 2, solid: false, vulnerable: false, data: state,
    })
    actor.vel.copy(dir).multiplyScalar(projectile.speed)
    actor.roomId = source.roomId
    this.projectileIds.add(actor.id)
  }

  private updateProjectiles(ctx: Ctx): void {
    for (const id of [...this.projectileIds]) {
      const projectile = ctx.actors.get(id)
      if (!projectile || !projectile.alive) {
        this.projectileIds.delete(id)
        continue
      }
      const state = projectile.data as ProjectileState
      if (!state || --state.lifetime < 0) {
        ctx.actors.remove(id)
        this.projectileIds.delete(id)
        continue
      }
      const intendedDistance = projectile.vel.length() * ctx.dt
      const distance = state.rangeRemaining === null ? intendedDistance : Math.min(intendedDistance, state.rangeRemaining)
      if (distance <= 0) continue
      _step.copy(projectile.vel).normalize()
      const worldHit = ctx.world.raycast(projectile.pos, _step, distance)
      const owner = ctx.actors.get(state.ownerId) ?? null
      const targetFaction = projectile.faction === 'player' ? 'enemy' : 'player'
      const actorHit = ctx.actors.raycast(projectile.pos, _step, worldHit?.distance ?? distance, owner, targetFaction)
      if (actorHit) {
        ctx.actors.damage(actorHit.actor, state.damage, owner, _step.clone(), state.damageKind)
        this.projectileImpact(ctx, projectile, state, actorHit.point)
      } else if (worldHit) {
        ctx.fx.impact(worldHit.point, worldHit.normal, worldHit.impact)
        this.projectileImpact(ctx, projectile, state, worldHit.point)
      } else {
        projectile.pos.addScaledVector(_step, distance)
        projectile.roomId = ctx.world.roomAt(projectile.pos.x, projectile.pos.z, projectile.roomId)
        if (state.rangeRemaining !== null) {
          state.rangeRemaining -= distance
          if (state.rangeRemaining <= 1e-4) {
            ctx.actors.remove(projectile.id)
            this.projectileIds.delete(projectile.id)
          }
        }
      }
    }
  }

  private projectileImpact(ctx: Ctx, projectile: Actor, state: ProjectileState, point: THREE.Vector3): void {
    if (state.splashRadius > 0) {
      ctx.actors.splash(point, state.splashRadius, state.splashDamage, ctx.actors.get(state.ownerId) ?? null, state.damageKind, 0.64)
      ctx.fx.explosion(point, state.splashRadius, state.color)
      ctx.fx.sound('explosion', point, 0.9)
      ctx.fx.shake(0.2)
    } else {
      ctx.fx.explosion(point, 0.55, state.color)
    }
    ctx.actors.remove(projectile.id)
    this.projectileIds.delete(projectile.id)
  }
}
